<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import robWingateFlowers from './images/robWingateFlowers.jpg';
	import windowWithShadows from './images/windowWithShadows.jpeg';
	import womanInstallingSoundproofingWindowInsert from './images/womanInstallingSoundproofingWindowInsert.jpg';
	import womanNextToRadiator from './images/womanNextToRadiator.jpg';
</script>

<Section>
	<Image
		alt="A window with a sitting area overlooks a beautiful outdoor landscape with various trees and flowers."
		src={robWingateFlowers}
	>
		{#snippet descriptionSlot()}
			<p>
				<a
					href="https://unsplash.com/photos/Fd9tUmRBJzk"
					target="_blank"
					rel="external">Source</a
				>
			</p>
		{/snippet}
	</Image>

	<p>
		Insulating your apartment windows can help make your space more
		energy-efficient, comfortable, and budget-friendly. But if you’re renting
		the apartment you’re currently in, you might not want to splurge on
		expensive solutions for a drafty space that isn’t your forever home.
	</p>
	<p>
		Thankfully, though, there are a few ways to keep your apartment more
		energy-efficient with affordable, easy-to-install options available. We’ll
		break down a few of our favorite ways to insulate apartment windows here.
	</p>
</Section>

<Section>
	<h2>Why Bother with Insulation?</h2>
	<Image
		alt="A person is crouched next to a heater, holding an orange coffee mug."
		src={womanNextToRadiator}
	>
		{#snippet descriptionSlot()}
			<p>
				<a href="/resources/blog/polar-vortex-preparation">Source</a>
			</p>
		{/snippet}
	</Image>
	<p>
		Whether you’ve just moved into a new apartment or lived in your home for a
		few years, it’s a good idea to inspect your windows on an annual basis to
		ensure that they’re providing your space with a well-ventilated and
		well-insulated environment.
	</p>
	<p>
		Windows play an important role in protecting the home’s building envelope.
		To keep the home well-sealed, <a
			href="/resources/blog/polar-vortex-preparation">windows and doors</a
		> require regular maintenance to stay functional.
	</p>
	<p>
		Any air leaks in your window design can result in higher utility bills
		during the winter or summer. They also make it harder to call your apartment
		your “home sweet home” when you’re never quite comfortable with the
		temperature.
	</p>
	<p>
		“If your existing windows are in good condition, taking steps to reduce the
		energy loss through windows can make your home more comfortable and save you
		money on energy bills,” according to <a
			href="https://www.energy.gov/energysaver/update-or-replace-windows"
			target="_blank"
			rel="external">Energy.gov</a
		>.
	</p>
	<p>
		You can inspect the windows and doors to see if there are any leaks or gaps
		that need attention. “See if you can rattle them, since movement means
		possible air leaks. If you can see daylight around a door or window frame,
		then the door or window leaks. You can usually seal these leaks by caulking
		or weather-stripping them,” suggest experts at <a
			href="https://www.energy.gov/energysaver/update-or-replace-windows"
			target="_blank"
			rel="external">Energy.gov</a
		>.
	</p>
</Section>

<Section>
	<h2>Options for Insulating Windows</h2>
	<Image
		alt="An Indow window insert installed on a window. "
		src={windowWithShadows}
	>
		{#snippet descriptionSlot()}
			<p>
				<a href="/resources/blog/how-to-fix-a-cold-room">Source</a>
			</p>
		{/snippet}
	</Image>
	<p>
		There are a few products and methods that make it easy for anyone to
		insulate their windows for optimal functionality — even if you have a
		smaller budget.
	</p>
	<h3>Rope Caulking</h3>
	<p>
		If you’re in a pinch, rope caulking is a rubber-based rope material that
		offers a cheap yet effective solution for sealing bigger gaps in windows.
		There are a few brands of rope caulking out there to choose from. Simply
		follow the instructions on the product packaging for installation.
	</p>
	<h3>Bubble Wrap Insulation</h3>
	<p>
		Bubble wrap offers another quick and budget-friendly solution for
		apartment-dwellers. While it might not be the most stylish strategy for
		insulation, placing the bubble side of the material against wet glass will
		add a layer of protection to the windows. “The still layer of air trapped in
		the bubbles gives a cheap double-glazed-type effect,” scientist Jenny
		Edwards told <a
			href="https://www.abc.net.au/news/2017-01-30/keeping-your-house-cool-this-summer-with-bubble-wrap/8222632"
			target="_blank"
			rel="external">ABC Radio</a
		>.
	</p>
	<h3>Thermal Curtains</h3>
	<p>
		Thermal curtains feature a special lining that helps trap warm air inside
		the layers of fabric. They also offer a more aesthetically appealing
		solution to apartment dwellers. For optimal impact, any curtains you select
		will need to cover the length and width of the entire window.
	</p>
	<h3>Window Inserts</h3>
	<p>
		Window inserts are an easy-to-install and more permanent solution for
		insulation, in which a panel is simply inserted on top of the original
		window. Window inserts can be a more expensive option than bubble wrap or
		caulking, but their custom sizes and durable features make them a viable
		long-term option for <a href="/resources/blog/polar-vortex-preparation"
			>insulating apartment windows</a
		>.
	</p>
	<p>
		<span class="italic"
			>“With any efficiency improvements, take steps to ensure proper
			installation and check for air leaks again after making the improvement,”</span
		> suggests Energy.gov.
	</p>
</Section>

<Section>
	<h2>Simple Solutions for Your Space</h2>
	<Image
		alt="A person wearing a pink sweater and glasses is installing window inserts."
		src={womanInstallingSoundproofingWindowInsert}
	>
		{#snippet descriptionSlot()}
			<p>
				<a href="/resources/blog/insulate-old-windows-without-replacing"
					>Source</a
				>
			</p>
		{/snippet}
	</Image>
	<Bubble>
		<p class="mb-8">
			There are a few ways that you can improve the comfort and livability of
			your apartment, and insulating windows is an easy way to help. If you’re
			after more dependable, long-term solutions, window inserts from Indow
			offer a few benefits:
		</p>
		<ul>
			<li>
				They’re made of durable materials that went through <a
					href="/performance">third party testing</a
				>
			</li>
			<li>
				They provide effective insulation to <a
					href="/resources/blog/insulate-old-windows-without-replacing"
					>protect older windows</a
				> from air leaks and moisture.
			</li>
			<li>
				They <a href="/resources/blog/how-to-fix-a-cold-room"
					>prevent heat transfer</a
				> in the building envelope.
			</li>
			<li>
				The inserts also <a href="/resources/faq/#durability"
					>offer soundproofing</a
				> from external noise, for a more comfortable indoor environment.
			</li>
			<li>
				They are backed by a <a href="/resources/faq/#durability"
					>limited lifetime warranty</a
				>.
			</li>
		</ul>
	</Bubble>
	<p>
		We know that you might not be willing to pay for inserts if you are only
		living in your apartment for a brief period of time. However, you can have a <a
			href="/solutions/reduce-energy-waste"
			>conversation with your landlord to get them onboard</a
		>. If they invest in Indow inserts, they will be giving their property an
		upgrade that will benefit future tenants. These inserts can quickly become a
		selling point for them, which would benefit them in the long run.
	</p>
	<p>
		If you do plan on staying in your apartment for a long period of time and
		decide to buy these inserts yourself, you’ll certainly be getting a bang for
		your buck. Our inserts will provide you with comfort for years to come, and
		you can order and install them whenever you’d like.
	</p>
	<p>
		The team of innovators at Indow works hard to create the best and most
		effective <a href="/">window inserts</a> on the market, improving the performance
		of windows in apartments, homes, and commercial buildings everywhere.
	</p>
	<p>
		Our window inserts are available across the U.S. and Canada, and we offer a <a
			href="/how-to/get-indow-inserts">free estimate</a
		> to help plan your insulation project.
	</p>
</Section>

<Reviews />
